import React, { useState, useRef } from "react";
import * as XLSX from "xlsx";
import { toast } from "react-hot-toast";
import { Button } from "../../components/ui";
import LinkButton from "../../components/ui/LinkButton";
import { FiUploadCloud } from "react-icons/fi";
import { InlineLoader } from "../../components/Loader";
import RoundedContainer from "../../components/containers/RoundedContainer";
import useExcelUpload from "../../hooks/useExcelUpload";
import useDownloadGetFile from "../../hooks/useDownloadGetFile";
import BreadCrumbs from "../../components/ui/BreadCrumbs";

const REQUIRED_COLUMNS = [
  "Employee Code",
  "Status",
  "In Day",
  "In Month",
  "In Year",
  "Out Day",
  "Out Month",
  "Out Year",
];

const UploadAttendance = ({ maxRows = 100 }) => {
  const breadcrumbs = [
    {
      label: "Attendance",
      link: "/attendance",
    },
    {
      label: "Upload Attendance",
      link: "",
    },
  ];
  const [file, setFile] = useState(null);
  const [data, setData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [missingColumns, setMissingColumns] = useState([]);

  const fileInputRef = useRef(null);

  const { download: downloadTemplate, isLoading: isDownloadingTemplate } =
    useDownloadGetFile({
      url: "downloadAttendanceExcelTemplate",
      filename: "attendance_template.xlsx",
      onSuccess: () => {
        toast.success("Template downloaded successfully!");
      },
      onError: (error) => {
        toast.error("Failed to download template. Please try again.");
      },
    });

  const { upload, isLoading: isUploading } = useExcelUpload({
    url: "excelUploadAttendance",
    onSuccess: (response) => {
      toast.success(response?.data?.message || "Attendance uploaded successfully!");
      setFile(null);
      setData([]);
      setHeaders([]);
      setValidationErrors({});
      setMissingColumns([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || error.message || "Failed to upload attendance file. Please try again.";
      toast.error(errorMessage);
    },
  });

  const convertDecimalTimeToString = (value) => {
    // If it's a number (decimal representation of time in Excel), convert to HH:MM
    if (typeof value === "number" && value >= 0 && value < 1) {
      const hours = Math.floor(value * 24);
      const minutes = Math.round((value * 24 - hours) * 60);
      return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
    }
    // If it's already a string or other format, return as-is
    return value;
  };

  const normalizeTimeColumns = (data) => {
    // Columns that might contain time values
    const timeColumns = ["In Time", "Out Time"];

    return data.map((row) => {
      const normalizedRow = { ...row };
      timeColumns.forEach((col) => {
        if (normalizedRow[col] !== undefined && normalizedRow[col] !== null) {
          normalizedRow[col] = convertDecimalTimeToString(normalizedRow[col]);
        }
      });
      return normalizedRow;
    });
  };

  const handleFileUpload = (files) => {
    const uploadedFile = files?.[0];
    if (!uploadedFile) return;

    const validExtensions = [".xlsx", ".xls"];
    const fileExtension = uploadedFile.name
      .substring(uploadedFile.name.lastIndexOf("."))
      .toLowerCase();

    if (!validExtensions.includes(fileExtension)) {
      toast.error("Please upload a valid Excel file (.xlsx or .xls)");
      return;
    }

    setFile(uploadedFile);
    setValidationErrors({});
    setMissingColumns([]);

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const bstr = event.target.result;
        const wb = XLSX.read(bstr, { type: "binary" });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];

        // Extract headers first to handle empty data scenario
        const sheetDataArray = XLSX.utils.sheet_to_json(ws, { header: 1 });
        let parsedHeaders = [];
        if (sheetDataArray.length > 0) {
          parsedHeaders = sheetDataArray[0].filter(h => h); // Filter out empty cells
        }

        const parsedData = XLSX.utils.sheet_to_json(ws, { defval: "" });

        // Normalize time columns to ensure consistency
        const normalizedData = normalizeTimeColumns(parsedData);

        setHeaders(parsedHeaders);
        setData(normalizedData);
      } catch (err) {
        console.error("Error parsing the Excel file:", err);
        toast.error("An error occurred while parsing the Excel file.");
      }
    };
    reader.onerror = (error) => {
      toast.error("Failed to read the file.");
    }
    reader.readAsBinaryString(uploadedFile);
  };

  const handleInputChange = (e) => {
    if (e.target.files) {
      handleFileUpload(e.target.files);
    }
  };

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      // Clear the value so selecting the same file twice triggers onChange
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const handleDownloadTemplate = () => {
    downloadTemplate.mutate();
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileUpload(e.dataTransfer.files);
    }
  };

  const validateData = () => {
    if (data.length === 0) {
      toast.error("The uploaded file is empty.");
      return false;
    }

    if (data.length > maxRows) {
      toast.error(`Maximum row limit of ${maxRows} exceeded. Please reduce the number of rows.`);
      return false;
    }

    // Check for missing required columns
    const missing = REQUIRED_COLUMNS.filter((col) => !headers.includes(col));

    if (missing.length > 0) {
      setMissingColumns(missing);
      toast.error(`Missing required columns: ${missing.join(", ")}`);
      return false;
    }

    let isValid = true;
    const errors = {};

    data.forEach((row, rowIndex) => {
      REQUIRED_COLUMNS.forEach((col) => {
        const val = row[col];
        const isEmpty = val === undefined || val === null || val.toString().trim() === "";
        if (isEmpty) {
          if (!errors[rowIndex]) errors[rowIndex] = {};
          errors[rowIndex][col] = true;
          isValid = false;
        }
      });
    });

    if (!isValid) {
      setValidationErrors(errors);
      toast.error("There are empty cells in required columns. Please fix them before submitting.");
      return false;
    }

    return true;
  };

  const handleSubmit = () => {
    const isValid = validateData();
    if (isValid && file) {
      upload.mutate(file);
    }
  };

  const handleCellClick = (rowIndex, col, isError) => {
    if (isError) {
      toast.error(
        `Row ${rowIndex + 1}, Column "${col}" cannot be empty. Please fill it in the Excel file and reupload.`,
        { id: `error-${rowIndex}-${col}` }
      );
    }
  };

  return (
    <section className="pt-10 pb-10 px-4">
      <BreadCrumbs crumbs={breadcrumbs} />
      <div className="container mb-4">
        <div className="flex justify-end px-4">
          <Button
            onClick={handleDownloadTemplate}
            disabled={isDownloadingTemplate}
          >
            {isDownloadingTemplate && <InlineLoader className="text-white mr-2" />}
            Download Template
          </Button>
        </div>
      </div>
      <RoundedContainer className="mx-4">
        <div className="flex flex-col gap-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-800">
              Upload Attendance
            </h2>
          </div>

          <div className="flex flex-col gap-4">
            <div
              className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-10 bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
              onClick={handleButtonClick}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <FiUploadCloud className="w-12 h-12 text-gray-400 mb-4" />
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleInputChange}
                ref={fileInputRef}
                className="hidden"
                onClick={(e) => e.stopPropagation()}
              />
              <Button onClick={(e) => {
                e.stopPropagation();
                handleButtonClick();
              }} variant="outline">
                Select Excel File
              </Button>
              <p className="text-sm text-gray-500 mt-2">or drag and drop your file here</p>
              {file && (
                <p className="mt-4 text-sm text-green-600 font-medium">
                  Selected File: {file.name}
                </p>
              )}
            </div>
          </div>

          {missingColumns.length > 0 && (
            <div className="bg-red-50 text-red-700 p-4 rounded-md border border-red-200">
              <p className="font-semibold">Missing Required Columns:</p>
              <ul className="list-disc pl-5 mt-2">
                {missingColumns.map((col) => (
                  <li key={col}>{col}</li>
                ))}
              </ul>
            </div>
          )}

          {headers.length > 0 && (
            <div className="flex flex-col gap-4 mt-2">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Preview Excel File</h3>
                <p className="text-sm text-gray-500 mt-1">Review your data before submitting. Required columns are marked with an asterisk (*).</p>
              </div>

              {data.length > maxRows && (
                <div className="bg-red-50 text-red-700 p-4 rounded-md border border-red-200">
                  <p className="font-semibold">Maximum Row Limit Exceeded</p>
                  <p className="text-sm">
                    You have uploaded {data.length} rows, but the maximum allowed is {maxRows} rows.
                    Please reduce the number of rows and try again.
                  </p>
                </div>
              )}
              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="p-3 border-b border-gray-200 font-semibold text-sm text-gray-600">
                        Row
                      </th>
                      {headers.map((header) => (
                        <th
                          key={header}
                          className="p-3 border-b border-gray-200 font-semibold text-sm text-gray-600 whitespace-nowrap"
                        >
                          {header}
                          {REQUIRED_COLUMNS.includes(header) && (
                            <span className="text-red-500 ml-1">*</span>
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.length === 0 ? (
                      <tr>
                        <td colSpan={headers.length + 1} className="p-5 text-center text-gray-500">
                          No data rows found in the uploaded file.
                        </td>
                      </tr>
                    ) : (
                      data.map((row, rowIndex) => (
                        <tr
                          key={rowIndex}
                          className={
                            rowIndex % 2 === 0 ? "bg-white" : "bg-gray-50"
                          }
                        >
                          <td className="p-3 border-b border-gray-200 text-sm text-gray-500">
                            {rowIndex + 1}
                          </td>
                          {headers.map((header) => {
                            const isRequired = REQUIRED_COLUMNS.includes(header);
                            const isError =
                              validationErrors[rowIndex]?.[header];

                            return (
                              <td
                                key={`${rowIndex}-${header}`}
                                onClick={() =>
                                  handleCellClick(rowIndex, header, isError)
                                }
                                className={`p-3 border-b border-gray-200 text-sm whitespace-nowrap ${isError
                                  ? "bg-red-100 border-2 border-red-500 cursor-pointer text-red-700 font-medium"
                                  : "text-gray-700"
                                  }`}
                              >
                                {row[header] || ""}
                                {isError && (
                                  <span className="text-red-500 text-xs ml-2">
                                    Empty
                                  </span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      )))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end mt-4">
                <Button
                  onClick={handleSubmit}
                  disabled={isUploading || !file || data.length === 0 || data.length > maxRows}
                  className="w-40"
                >
                  {isUploading && <InlineLoader className="text-white mr-2" />}
                  {isUploading ? "Uploading..." : "Submit"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </RoundedContainer>
    </section>
  );
};

export default UploadAttendance;
